import type { Bootstrap, CurrentUser } from '@capsio/contracts';
import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api, messageOf } from './api-client.js';
import { AppShell } from './app/AppShell.js';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js';
import { InvitePage } from './pages/InvitePage.js';
import { LoginPage } from './pages/LoginPage.js';
import { TerminologyPage } from './pages/TerminologyPage.js';
import { SetupPage } from './pages/SetupPage.js';

type Boot =
  | { phase: 'loading' }
  | { phase: 'failed'; message: string }
  | { phase: 'ready'; bootstrap: Bootstrap; user: CurrentUser | null };

export function App(): ReactNode {
  const [boot, setBoot] = useState<Boot>({ phase: 'loading' });
  const location = useLocation();

  /** 初始化完成、登录、退出、改发信配置之后都要重探一次，累加它触发效果重跑。 */
  const [reloadKey, setReloadKey] = useState(0);
  const refresh = (): void => setReloadKey((key) => key + 1);

  /**
   * 首屏要同时知道两件事：这套部署初始化了没有，以及当前有没有登录态。
   * 两个请求并发发出，任何一个失败都当「连不上后端」处理——那时候页面也没什么可渲染的。
   */
  useEffect(() => {
    let cancelled = false;
    void Promise.all([api.bootstrap(), api.session()]).then(
      ([bootstrap, session]) => {
        if (!cancelled) setBoot({ phase: 'ready', bootstrap, user: session.user });
      },
      (cause: unknown) => {
        if (!cancelled) setBoot({ phase: 'failed', message: messageOf(cause) });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  if (boot.phase === 'loading') {
    return (
      <div className="centered">
        <p className="code">connecting</p>
        <p>正在连接后端…</p>
      </div>
    );
  }

  if (boot.phase === 'failed') {
    return (
      <div className="centered">
        <p className="code">backend unreachable</p>
        <h1>连不上后端</h1>
        <p>{boot.message}</p>
      </div>
    );
  }

  const { bootstrap, user } = boot;

  /**
   * 企业信息在设置里改过了，重新探一次。
   *
   * 只换 bootstrap，不动登录态——改企业名跟「你是谁」无关，
   * 顺手把 user 也刷一遍会让界面无谓地闪一下。
   */
  function refreshOrganization(): void {
    void api.bootstrap().then(
      (next) => setBoot({ phase: 'ready', bootstrap: next, user }),
      () => undefined,
    );
  }

  const signedIn = (next: CurrentUser): void => {
    setBoot({ phase: 'ready', bootstrap, user: next });
  };

  // 还没初始化：除了 /setup 哪儿都去不了，因为系统里连一个账号都没有。
  if (!bootstrap.initialized) {
    return (
      <Routes>
        <Route path="/setup" element={<SetupPage onDone={refresh} />} />
        <Route path="*" element={<Navigate to="/setup" replace />} />
      </Routes>
    );
  }

  const organizationName = bootstrap.organizationName ?? 'Capsio Hub';

  if (user === null) {
    return (
      <Routes>
        {/* 已初始化后 /setup 不再存在，跳登录。 */}
        <Route path="/setup" element={<SealedSetupNotice />} />
        <Route path="/invite/:token" element={<InvitePage onSignedIn={signedIn} />} />
        <Route
          path="/forgot-password"
          element={
            <ForgotPasswordPage mailConfigured={bootstrap.mailConfigured} onSignedIn={signedIn} />
          }
        />
        <Route
          path="/login"
          element={<LoginPage organizationName={organizationName} onSignedIn={signedIn} />}
        />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: location }} />} />
      </Routes>
    );
  }

  /*
   * 术语页走独立路由（C-167 A7）。
   *
   * **刻意不挂在 AppShell 里面**：那份文件今天由另一个窗口在改，
   * 而术语页与资产树、页签条没有任何关系——它不需要左栏，也不该占那一屏。
   * 独立路由让这一页零冲突地加进来，也让它能被单独收藏与转发。
   */
  return (
    <Routes>
      <Route path="/terminology" element={<TerminologyPage />} />
      <Route
        path="*"
        element={
          <AppShell
            user={user}
            organizationName={organizationName}
            organizationCode={bootstrap.organizationCode ?? ''}
            onOrganizationChanged={refreshOrganization}
            onUserChanged={signedIn}
            onSignedOut={refresh}
          />
        }
      />
    </Routes>
  );
}

function SealedSetupNotice(): ReactNode {
  const [seconds, setSeconds] = useState(3);

  useEffect(() => {
    if (seconds <= 0) {
      window.location.replace('/login');
      return;
    }
    const timer = setTimeout(() => setSeconds(seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [seconds]);

  return (
    <div className="centered">
      <p className="code">GET /setup → /login</p>
      <h1>初始化入口已关闭</h1>
      <p>系统已经初始化过了，这个页面不再存在。{seconds} 秒后跳转到登录页。</p>
    </div>
  );
}
